import {
  DataRetentionPolicyDetailCategory,
  RetentionOptions,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";

// The built-in retention purposes and the sentences they publish. Kept free of UI imports so the
// step logic (retentionPolicySteps.ts) and its tests can use it directly.
export const descriptionForTypeAndPeriod = (
  type: DataRetentionPolicyDetailCategory | undefined,
  period: RetentionOptions,
): string | null => {
  if (!type) {
    return null;
  }

  const template = DATA_RETENTION_CATEGORIES[type]?.description;
  if (!template) {
    return null;
  }

  // Special handling for INDEFINITE - use a fixed message and append the trailing text
  if (period === "INDEFINITE") {
    let baseMessage =
      "We keep as long as necessary to fulfill the purpose for which we collect it.";

    // Extract any text that comes after the "YEARS" placeholder in the template
    // This includes additional clauses like "We may keep data beyond this period..."
    if (template.includes("YEARS")) {
      const parts = template.split("YEARS");
      if (parts.length > 1) {
        // Get the text after "YEARS" and clean up the beginning
        let trailingText = parts[1];
        // Remove common time-related prefixes like "of collecting it", "from your last purchase with us", etc.
        trailingText = trailingText
          .replace(/^[^.]*?\.\s*/, " ") // Remove everything up to and including the first period, then add space
          .trim();

        if (trailingText) {
          baseMessage += " " + trailingText;
        }
      }
    }

    return baseMessage;
  }

  const replacement = yearsForPeriod(period);
  if (replacement == null) {
    return null;
  }

  return template.replace("YEARS", replacement);
};

const yearsForPeriod = (period: RetentionOptions): string | null => {
  switch (period) {
    case "YEARS_2":
      return "2 years";
    case "YEARS_5":
      return "5 years";
    case "YEARS_7":
      return "7 years";
    case "YEARS_10":
      return "10 years";
    case "INDEFINITE":
      return "indefinitely";
    case "CUSTOM":
      return null;
  }
};

type BuiltinPolicy = {
  label: string;
  strong: string;
  description: string;
  suggested: string[];
  displayOrder: number;
};

export const DATA_RETENTION_CATEGORIES: Record<DataRetentionPolicyDetailCategory, BuiltinPolicy> = {
  ONLINE_DATA_FROM_WEBSITE: {
    label: "Cookies and online data we collect while you use our website",
    strong: "Cookies and online data",
    description:
      "We delete or anonymize data concerning your use of our website within YEARS of collecting it. Cookies and on-device data can be deleted at any time by you, the consumer.",
    suggested: ["online-identifiers", "internet-activity", "geolocation-information"],
    displayOrder: 1,
  },
  PROCESS_AND_SHIP_ORDERS: {
    label: "Data we collect in order to process and ship orders you place with us",
    strong: "process and ship orders",
    description:
      "We keep personal information related to products and services you purchase for as long as the personal data is required for us to fulfill our contract with you, and for YEARS from your last purchase with us. We may keep data beyond this period in anonymized form.",
    suggested: [
      "name",
      "email-address",
      "postal-address",
      "purchases",
      "phone-number",
      "credit-debit-card-number",
    ],
    displayOrder: 2,
  },
  CONTACT_CUSTOMER_SUPPORT: {
    label: "Data we collect when you contact us for customer support and other inquiries",
    strong: "customer support and other inquiries",
    description:
      "We keep customer feedback and correspondence with our customer service for up to YEARS to help us respond to any questions or complaints. We may keep data beyond this period in anonymized form.",
    suggested: [
      "name",
      "email-address",
      "purchases",
      "phone-number",
      "direct-communications-with-your-consumers",
    ],
    displayOrder: 3,
  },
  SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING: {
    label: "Data we collect when you sign up for promotional and marketing communications",
    strong: "promotional and marketing communications",
    description:
      "Where you have signed up to receive promotional and marketing communications from us, we will retain any data collected until you opt out or request its deletion. We may keep data beyond this period in anonymized form. We will further retain a record of any opt-outs in order to prevent sending you future communications.",
    suggested: [
      "name",
      "email-address",
      "postal-address",
      "purchases",
      "phone-number",
      "online-identifiers",
      "internet-activity",
      "inferences",
      "direct-communications-with-your-consumers",
    ],
    displayOrder: 4,
  },
  REVIEW_PRODUCTS_ANSWER_SURVEYS_SEND_FEEDBACK: {
    label: "Data we collect when you review our products, answer surveys, or send feedback",
    strong: "review our products, answer surveys, or send feedback",
    description:
      "We retain review, survey, and feedback data for up to YEARS following your last contact with us. We may keep data beyond this period in anonymized form to help improve our products and services.",
    suggested: ["name", "email-address", "purchases", "direct-communications-with-your-consumers"],
    displayOrder: 5,
  },
  PRIVACY_REQUESTS: {
    label: "Data we collect in connection with privacy requests",
    strong: "privacy requests",
    description:
      "We retain records related to privacy requests as long as necessary to comply with our legal obligations, and for a minimum of 24 months.",
    suggested: ["name", "email-address", "online-identifiers"],
    displayOrder: 6,
  },
  SECURITY_PURPOSES: {
    label: "Data we collect for security purposes",
    strong: "security purposes",
    description:
      "We retain security-related data as long as necessary to comply with our legal obligations and to maintain and improve our information security measures.",
    suggested: ["name", "email-address", "online-identifiers"],
    displayOrder: 7,
  },
};
